import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AgencyDashboardData } from '../api/dashboard-api';

// Tipos de acciones para las sagas
export interface GetDashboardDataRequest {
  // Sin parámetros por ahora, puede expandirse en el futuro
}

// Tipo extendido para incluir todas las métricas del dashboard
export interface ExtendedDashboardData extends AgencyDashboardData {
  agentsMetrics?: {
    totalAgents: number;
    activeAgents: number;
    topPerformers: Array<{
      id: string;
      name: string;
      sales: number;
      commission: number;
      leads: number;
    }>;
    averagePerformance: {
      salesPerAgent: number;
      commissionPerAgent: number;
      leadsPerAgent: number;
    };
  };
  financialSummary?: {
    monthlyRevenue: number;
    monthlyExpenses: number;
    netIncome: number;
    outstandingCommissions: number;
    totalAssets: number;
    cashFlow: Array<{
      month: string;
      income: number;
      expenses: number;
      net: number;
    }>;
  };
  alerts?: {
    pendingLeads: number;
    expiringListings: number;
    overdueCommissions: number;
    inactiveAgents: number;
    lowInventory: boolean;
  };
  statsByPeriod?: {
    '7d'?: {
      properties: number;
      leads: number;
      revenue: number;
      conversions: number;
      growth: number;
    };
    '30d'?: {
      properties: number;
      leads: number;
      revenue: number;
      conversions: number;
      growth: number;
    };
  };
  leadsTrends?: {
    leadsBySource: Record<string, number>;
    leadsByStage: Record<string, number>;
    conversionFunnel: Array<{
      stage: string;
      count: number;
      percentage: number;
    }>;
    monthlyTrends: Array<{
      month: string;
      newLeads: number;
      convertedLeads: number;
      conversionRate: number;
    }>;
  };
  leadsStats?: {
    new: number;
    active: number;
    closed: number;
  };
  topViewedProperties?: Array<{
    id: string;
    title: string;
    views: number;
    price: number;
  }>;
  projectedRevenue?: number;
  actualRevenue?: number;
  performanceMetrics?: {
    averageDealSize: number;
    conversionRate: number;
    agentProductivity: number;
    clientSatisfaction: number;
    marketShare: number;
  };
  campaignsStats?: {
    total: number;
    byStatus: Record<string, number>;
    byType: Record<string, number>;
    activeCampaigns?: number;
  };
  _calculatedGrowth?: {
    properties: number;
    agents: number;
    clients: number;
    revenue: number;
  };
}

// Estado inicial
export interface DashboardState {
  data: ExtendedDashboardData | null;
  loading: boolean;
  error: string | null;
}

const initialState: DashboardState = {
  data: null,
  loading: false,
  error: null,
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    // Acción que dispara la saga
    getDashboardDataRequest: (state, _action: PayloadAction<GetDashboardDataRequest>) => {
      state.loading = true;
      state.error = null;
    },

    // Acción que actualiza el estado cuando se obtiene exitosamente
    getDashboardDataSuccess: (state, action: PayloadAction<ExtendedDashboardData>) => {
      state.loading = false;
      state.data = action.payload;
      state.error = null;
    },

    // Acción que actualiza el estado cuando hay un error
    dashboardFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Limpiar errores
    clearDashboardError: (state) => {
      state.error = null;
    },

    // Limpiar datos del dashboard
    clearDashboardData: (state) => {
      state.data = null;
      state.error = null;
    },
  },
});

export const {
  getDashboardDataRequest,
  getDashboardDataSuccess,
  dashboardFailure,
  clearDashboardError,
  clearDashboardData,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;

